import { AdditiveBlending, Color, DoubleSide, ShaderMaterial } from "three";

const vertexShader = /* glsl */ `
in vec3 aBary;
// Por instância (os lotes de \`SkillBatch\`): posição, giro (quatérnio), escala − 1 (em w o 1 que a
// completa), cor linear e opacidade, e os parâmetros do efeito. Uma malha solta (fita, orbe) não tem
// esses atributos e recebe os padrões do material: identidade, cor 0 e opacidade 1, parâmetros 0.
in vec3 iOffset;
in vec4 iRot;
in vec4 iScale;
in vec4 iColor;
in vec4 iParams;
out vec3 vBary;
out vec2 vUv;
out vec4 vColor;
out vec4 vParams;
vec3 rotateQ(vec4 q, vec3 v) {
  return v + 2.0 * cross(q.xyz, cross(q.xyz, v) + q.w * v);
}
void main() {
  vBary = aBary;
  vUv = uv;
  vColor = iColor;
  vParams = iParams;
  vec3 p = rotateQ(iRot, position * (iScale.xyz + iScale.www)) + iOffset;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
}
`;

const fragmentShader = /* glsl */ `
uniform vec3 uColor;
uniform float uAlpha;
uniform float uGlow;
uniform float uFill;
uniform float uZone;
in vec3 vBary;
in vec2 vUv;
in vec4 vColor;
in vec4 vParams;
void main() {
  // A cor e a opacidade somam o uniforme (malha solta) e a instância (lote): um dos dois vale 0/1.
  vec3 color = uColor + vColor.rgb;
  float alpha;
  float bright;
  if (uZone > 0.5) {
    // Zona, onda ou clarão no chão (F65, F77): progresso, marcas da classe, onda (z > 0,5), clarão (w > 1,5).
    float progress = vParams.x;
    vec2 p = vUv * 2.0 - 1.0;
    float r = length(p);
    float aa = fwidth(r) * 1.5;
    float a = atan(p.y, p.x);
    // Marcas na borda pela geometria da classe (6 hexágonos, 3 triângulos, 12 nós, 4 linhas).
    float marks = 0.5 + 0.5 * cos(a * vParams.y);
    float tick = smoothstep(0.85, 0.98, marks);
    if (vParams.w > 2.5) {
      // Estrela do impacto (Fase 14, a referência): de frente para a câmera, um núcleo branco e raios finos
      // na cor de quem bateu (y: quantos), mais um segundo jogo de raios curtos entre eles; acende na hora e
      // apaga ao quadrado. Acima de 1 (bright), o bloom abre o clarão.
      float n = max(vParams.y, 2.0);
      float fade = 1.0 - progress;
      float core = exp(-r * r * 22.0) * fade;
      float halo = exp(-r * r * 4.0) * 0.3 * fade;
      float reach = 1.0 - smoothstep(0.0, 1.0, r / (0.4 + 0.6 * sqrt(progress + 0.05)));
      // Raios afinando para fora (a largura cai com r): fios de luz, não pétalas.
      float main = pow(abs(cos(a * n * 0.5)), 14.0 + 50.0 * r) * reach;
      float minor = pow(abs(sin(a * n * 0.5)), 30.0 + 60.0 * r) * (1.0 - smoothstep(0.0, 0.5, r));
      alpha = clamp(core + halo + (main + minor * 0.7) * fade, 0.0, 1.0);
      color = mix(color, vec3(1.0), min(1.0, core * 1.2));
    } else if (vParams.w > 1.5) {
      // Clarão (F77, Flecha Sinalizadora): luz no chão, mais forte no centro; acende logo e apaga no fim.
      float light = 1.0 - smoothstep(0.0, 1.0, r);
      alpha = light * light * 0.55 * smoothstep(0.0, 0.04, progress) * (1.0 - smoothstep(0.7, 1.0, progress));
    } else if (vParams.z > 0.5) {
      // Onda: um anel que sai do centro até a borda e apaga.
      float ring = smoothstep(progress - 0.08 - aa, progress - 0.04, r) * (1.0 - smoothstep(progress, progress + aa, r));
      alpha = (ring * (0.7 + 0.3 * tick)) * (1.0 - progress);
    } else {
      // Zona aliada: só o contorno (o hostil é que enche, doc 09 §2.2) e as marcas; o preenchimento leve
      // cresce até a borda pelo tempo do efeito.
      float edge = smoothstep(0.9 - aa, 0.93, r) * (1.0 - smoothstep(0.97, 0.97 + aa, r));
      float ticks = tick * smoothstep(0.8, 0.86, r) * (1.0 - smoothstep(0.97, 0.97 + aa, r));
      float fill = (1.0 - smoothstep(progress - aa, progress, r)) * 0.07;
      float fade = 1.0 - smoothstep(0.85, 1.0, progress);
      alpha = max(max(edge * 0.8, ticks), fill) * fade;
    }
    bright = vParams.w > 2.5 ? 3.0 : 1.8;
  } else {
    // Malha neon (guia §18, doc 09 §3.4): arestas pela coordenada baricêntrica (largura constante na tela) e
    // a face quase transparente; acima de 1, o bloom pega (nós, pico do impacto).
    // Componente constante (aresta escondida, \`withBary\`): fwidth 0, e smoothstep(0, 0, x) é indefinido.
    vec3 d = max(fwidth(vBary), vec3(1e-4));
    vec3 e = smoothstep(vec3(0.0), d * 1.5, vBary);
    float edge = 1.0 - min(min(e.x, e.y), e.z);
    float fill = uFill + vParams.y;
    float glow = uGlow + vParams.x;
    float rim = 0.0;
    if (vParams.w > 0.5) {
      // Folha do golpe (Fase 14, a meia-lua da referência): z é o lado (0 no cabo, 1 na ponta da energia),
      // w − 1 a idade (0 na ponta viva, 1 no corte da cauda). A face e as bordas crescem para fora e
      // apagam com a idade; a borda de fora brilha mesmo longe da aresta (o gume de luz).
      float across = clamp(vParams.z, 0.0, 1.0);
      float life = 1.0 - clamp(vParams.w - 1.0, 0.0, 1.0);
      edge *= mix(0.15, 1.0, across) * life;
      fill *= (0.2 + 2.2 * across * across) * life * life;
      rim = smoothstep(0.72, 1.0, across) * life * life;
      fill = max(fill, rim * 0.55);
    }
    alpha = max(edge, fill);
    bright = mix(1.0, glow, max(edge, rim));
  }
  alpha *= uAlpha * vColor.w;
  if (alpha <= 0.003) discard;
  gl_FragColor = vec4(color * bright, alpha);
  #include <colorspace_fragment>
}
`;

/** Marcas de cada forma de classe nas zonas (doc 09 §3.4). */
export const SHAPE_MARKS = { hexagonos: 6, triangulos: 3, circulos: 12, linhas: 4 } as const;

/** Atributos por instância que uma malha solta não tem: os padrões deixam valer os uniformes. */
const INSTANCE_DEFAULTS = { iOffset: [0, 0, 0], iRot: [0, 0, 0, 1], iScale: [0, 0, 0, 1], iColor: [0, 0, 0, 1], iParams: [0, 0, 0, 0] };

function skillMaterial(uniforms: { color: Color; alpha: number; glow: number; fill: number; zone: boolean }): ShaderMaterial {
  const material = new ShaderMaterial({
    name: "peca-de-habilidade",
    uniforms: { uColor: { value: uniforms.color }, uAlpha: { value: uniforms.alpha }, uGlow: { value: uniforms.glow }, uFill: { value: uniforms.fill }, uZone: { value: uniforms.zone ? 1 : 0 } },
    vertexShader,
    fragmentShader,
    transparent: true,
    depthWrite: false,
    // Os dois lados também nas zonas: o lado entra na chave do programa (um programa para todos).
    side: DoubleSide,
    blending: AdditiveBlending,
  });
  material.defaultAttributeValues = { ...material.defaultAttributeValues, ...INSTANCE_DEFAULTS };
  if (uniforms.zone) {
    material.polygonOffset = true;
    material.polygonOffsetFactor = -2;
    material.polygonOffsetUnits = -4;
  }
  return material;
}

/**
 * Material de uma malha solta de efeito (F78, F92, F98): a fita, o orbe, as vitrines. Um programa para
 * todos os efeitos de habilidade (as peças e as zonas em lote, `createSkillBatchMaterial`, dividem ele).
 * A geometria precisa de `aBary` (`withBary`, em `render/geometry/skill-shapes.ts`).
 */
export function createSkillMeshMaterial(hex: string, glow = 1.8, fill = 0.04): ShaderMaterial {
  return skillMaterial({ color: new Color(hex), alpha: 1, glow, fill, zone: false });
}

/**
 * Material dos lotes (Fase 12, Gate B): peças 3D (`zone` falso) ou zonas no chão (`zone`), com cor,
 * opacidade, transformação e parâmetros por instância. O mesmo programa das malhas soltas.
 */
export function createSkillBatchMaterial(zone: boolean): ShaderMaterial {
  return skillMaterial({ color: new Color(0, 0, 0), alpha: 1, glow: 0, fill: 0, zone });
}
